import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';

const Modal = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 420px;
  background: ${themeColors.colorWhite};
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.16);
  padding: ${pxToRem(16)};
  z-index: 1000;
  overflow: hidden;

  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    top: auto;
    width: 100%;
    max-height: 75vh;
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -12px 24px rgba(0, 0, 0, 0.12);
    animation: slideUp 180ms ease-out;
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const ScrollContent = styled.div`
  max-height: 55vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${pxToRem(12)};
  align-items: flex-start;
`;

const Title = styled.h3`
  font-weight: 600;
  font-size: 14px;
  color: ${themeColors.colorTextPrimary};
  text-align: left;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};
  margin-top: 4px;
  text-align: left;
  font-weight: 400;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;

  > svg {
    width: 20px;
    color: ${themeColors.colorTextPrimary};
  }
`;

const MOBILE_OFFSET = 60; // scroll offset below Buy button

const InfoModal = ({
  open,
  onClose,
  anchorRef,
  parentRef,
  title,
  subtitle,
  children,
}) => {
  const modalRef = useRef(null);

  // Mobile scroll below Buy button
  useEffect(() => {
    if (!open || !parentRef?.current || !modalRef?.current) return;

    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const buttonEl = parentRef.current;
    const modalEl = modalRef.current;
    if (!buttonEl || !modalEl) return;

    const observer = new ResizeObserver(() => {
      const viewportHeight = window.innerHeight;
      const modalHeight = modalEl.getBoundingClientRect().height;
      const buttonBottom =
        buttonEl.getBoundingClientRect().bottom + window.scrollY;

      const targetScroll =
        buttonBottom - (viewportHeight - modalHeight) + MOBILE_OFFSET;
      window.scrollTo({
        top: targetScroll > 0 ? targetScroll : 0,
        behavior: 'smooth',
      });
    });

    observer.observe(modalEl);
    return () => observer.disconnect();
  }, [open, parentRef, modalRef]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (!open) return;
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  // Click outside to close (desktop + mobile)
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        // ignore click if it comes from anchor (link)
        if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside); // mobile support
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open, anchorRef, onClose]);

  if (!open) return null;

  return (
    <Modal ref={modalRef}>
      <Header>
        <div>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </div>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
      </Header>
      <ScrollContent>{children}</ScrollContent>
    </Modal>
  );
};

InfoModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  anchorRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  parentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
};

export default InfoModal;
